// Phase 28 — Communication tab (F workstream).
//
// Three sub-sections rolled into one tab:
//   • Direct email composer (uses POST /admin/users/{id}/send-email → Phase 26 stack)
//   • Mail log for this user (GET /admin/users/{id}/mail-log)
//   • Internal admin notes (POST/GET/DELETE /admin/users/{id}/notes)

import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Mail, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

const STATUS_TONES = {
  sent: "bg-emerald-100 text-emerald-800",
  dry_run: "bg-slate-100 text-slate-700",
  failed: "bg-red-100 text-red-800",
  queued: "bg-amber-100 text-amber-800",
};

export default function CommunicationTab({ user, userId }) {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [mailLog, setMailLog] = useState({ items: [], total: 0, loading: true });
  const [notes, setNotes] = useState({ items: [], total: 0, loading: true });
  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const loadMailLog = useCallback(() => {
    setMailLog((s) => ({ ...s, loading: true }));
    api.get(`/admin/users/${userId}/mail-log`, { params: { limit: 25 } })
      .then(({ data }) => setMailLog({ items: data.items || [], total: data.total || 0, loading: false }))
      .catch(() => setMailLog({ items: [], total: 0, loading: false }));
  }, [userId]);

  const loadNotes = useCallback(() => {
    setNotes((s) => ({ ...s, loading: true }));
    api.get(`/admin/users/${userId}/notes`, { params: { limit: 50 } })
      .then(({ data }) => setNotes({ items: data.items || [], total: data.total || 0, loading: false }))
      .catch(() => setNotes({ items: [], total: 0, loading: false }));
  }, [userId]);

  useEffect(() => { loadMailLog(); loadNotes(); }, [loadMailLog, loadNotes]);

  const sendEmail = async () => {
    if (!subject.trim() || !html.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/send-email`, {
        subject: subject.trim(),
        html,
      });
      const ok = data?.status && data.status !== "failed";
      toast[ok ? "success" : "error"](`Status: ${data?.status || "unknown"}${data?.error ? ` · ${data.error}` : ""}`);
      if (ok) {
        setSubject("");
        setHtml("");
        loadMailLog();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/users/${userId}/notes`, { note: noteBody.trim() });
      toast.success("Note added");
      setNoteBody("");
      loadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/admin/users/${userId}/notes/${id}`);
      toast.success("Note deleted");
      loadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    }
  };

  return (
    <div className="space-y-4 pt-2" data-testid="communication-tab">
      {/* COMPOSE */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <Send className="h-3 w-3" /> Send direct email
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Composed email → {user?.email || "user"}. Goes through the standard Phase 26 / Resend pipeline (logged in mail_log).
        </p>
        <div className="grid gap-2">
          <Label className="text-xs">Subject</Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="Subject (max 200 chars)"
            data-testid="comm-subject-input"
            className="h-9 text-sm"
          />
          <Label className="text-xs">HTML body</Label>
          <Textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={8}
            placeholder="<p>Hi,</p><p>This is a direct admin message.</p>"
            data-testid="comm-html-textarea"
            className="font-mono text-xs"
          />
          <div className="flex items-center justify-end">
            <Button
              size="sm"
              onClick={sendEmail}
              disabled={sending || !subject.trim() || !html.trim()}
              data-testid="comm-send-btn"
            >
              {sending ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : <Send className="me-1 h-3.5 w-3.5" />}
              Send email
            </Button>
          </div>
        </div>
      </div>

      {/* MAIL LOG */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <Mail className="h-3 w-3" /> Mail log
            <span className="ms-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600" data-testid="comm-mail-log-total">{mailLog.total}</span>
          </div>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={loadMailLog} data-testid="comm-mail-log-refresh">
            Refresh
          </Button>
        </div>
        {mailLog.loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : mailLog.items.length === 0 ? (
          <div className="text-sm text-slate-400" data-testid="comm-mail-log-empty">No emails sent yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="comm-mail-log-list">
            {mailLog.items.map((m) => (
              <li key={m.id || m.resend_id} className="rounded-lg border border-slate-100 px-3 py-2" data-testid="comm-mail-log-item">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{m.subject || "(no subject)"}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_TONES[m.status] || "bg-slate-100 text-slate-600"}`}>{m.status || "—"}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{m.event_type || "—"}</span>
                  <span>{timeAgo(m.ts)}</span>
                </div>
                {m.error && <div className="mt-1 text-[11px] text-red-600">⚠ {m.error}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* INTERNAL NOTES */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <StickyNote className="h-3 w-3" /> Internal notes
          <span className="ms-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600" data-testid="comm-notes-total">{notes.total}</span>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Private to admin staff. Never shown to the user. Audit-logged on create/delete.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Add a note (max 2000 chars)"
            data-testid="comm-note-input"
            className="text-sm"
          />
          <Button
            size="sm"
            onClick={addNote}
            disabled={savingNote || !noteBody.trim()}
            data-testid="comm-note-add-btn"
            className="sm:self-end"
          >
            {savingNote ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : null}
            Add note
          </Button>
        </div>
        <div className="mt-3">
          {notes.loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : notes.items.length === 0 ? (
            <div className="text-sm text-slate-400" data-testid="comm-notes-empty">No notes yet.</div>
          ) : (
            <ul className="space-y-1.5" data-testid="comm-notes-list">
              {notes.items.map((n) => (
                <li key={n.id} className="group flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2" data-testid="comm-note-item">
                  <div className="min-w-0 flex-1">
                    <p className="whitespace-pre-wrap text-sm text-slate-800">{n.note}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {n.admin_email || "admin"} · {timeAgo(n.ts)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-red-600 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => deleteNote(n.id)}
                    data-testid={`comm-note-delete-${n.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
